import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { COL, SCREEN, RESERVE, TYPES, POSTERS, generatePoster, familyOf, isMicro, layoutPosterTitle, rng } from './lib/bauhaus-posters.js';
import { buildAtlas, atlasSize, glyphCell, glyphWidth, normaliseText, ATLAS } from './lib/bauhaus-glyphs.js';

// Contrat du rig partagé : interdit. Une affiche ne s'éclaire pas.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'bauhaus',
  family: 'graphic',
  danceability: 0.78,
  energyLevel: 0.65,
  bpmMin: 90,
  bpmMax: 135,
  movement: 0.70,
  tonality: 0.75,
  enabled: true
};

// BAUHAUS : une affiche moderniste vivante, imprimée. Sur un papier crème,
// 25 à 40 éléments (3 à 5 grandes formes dont une domine, souvent coupée par
// le bord ; 8 à 12 moyennes ; 15 à 25 micro-éléments : filets, rangées de
// points, petits arcs, hachures, damiers, cercles concentriques, flèches,
// croix ; une ou deux règles noires épaisses en diagonale), le titre du
// morceau en capitales géométriques monumentales, et chaque famille de
// formes qui JOUE UN INSTRUMENT. Règles R1 à R19 sauf R3 et R4 (style
// propre : fond de papier, couleurs d'impression).
//
// LES AFFICHES (lib/bauhaus-posters.js) : 16 écrites à la main (les 12 de la
// v1 réécrites + CONSTRUCTIVISTE, SUISSE, KANDINSKY, DE STIJL) et un
// générateur aux mêmes règles. La structure (grandes et moyennes formes,
// diagonale, titre) est écrite ; les micro-éléments sont posés par un
// remplisseur graine fixe selon le style de l'affiche. La case de la
// pochette (centre de composition, R19) reste vide.
//
// L'ALPHABET (lib/bauhaus-glyphs.js) : des capitales construites en traits
// et en arcs, champ de distance exact en atlas (calculé une fois, sans police
// système). Une lettrine peut monter à 38 % de la hauteur.
//
// LES INSTRUMENTS
//   KICK     grands cercles bleus : +12 % (attaque 80 ms, retour 250 ms) et un
//            anneau fin qui se détache et s'agrandit
//   BASSE    longues barres : glissent le long de leur axe selon bands.low
//   CAISSE   triangles jaunes : +30° sur les contretemps (2 et 4)
//   CHARLEYS rangées de points : s'allument l'un après l'autre à la croche
//   MÉLODIE  arcs, demi-cercles : s'ouvrent et se ferment avec bands.mid
//   ACCENTS  petits carrés rouges : apparaissent, un quart de tour, disparaissent
//            sur les temps forts (une moitié par mesure)
// Une famille par phrase prend la couleur de la pochette. Sans analyse
// (bands à 0) : une basse et une mélodie synthétiques sur l'horloge.
//
// LA MATIÈRE : rendu dans une cible privée en valeurs sRGB. Les encres
// MULTIPLIENT le papier (surimpression : bleu sur jaune = vert profond, rouge
// sur jaune = orange), plaque décalée de 1 à 2 px par affiche (repérage),
// trame de points visible de près, encre plus dense sur les bords, grain du
// papier. Le COLLAGE : deux niveaux de papiers découpés opaques, ombre portée
// douce de 3 px vers le bas à droite, légère parallaxe par niveau.
//
// LES TRANSITIONS (sur la phrase, en alternance)
//   PRINT  l'affiche s'imprime couleur par couleur : jaune (passe
//          horizontale qui efface l'ancienne), rouge, bleu, noir, un temps chacun
//   IRIS   un grand cercle de la nouvelle affiche s'ouvre depuis un cercle de l'ancienne
//   GLIDE  chaque forme glisse vers sa place (appariement par type, v1)
//   CUT    l'ancienne se fend le long d'une diagonale ; les deux moitiés
//          glissent hors de l'écran
// PRINT, IRIS et CUT gardent l'ancienne affiche dans la seconde cible (on
// échange les deux cibles, aucune copie).
//
// LE DROP (marque d'analyse score >= 0,7, ou director.mode 'drop') : 4
// mesures à pleine amplitude, cercles forts, triangles qui tournent, lettres
// du titre dispersées puis recomposées ; le papier s'éclaircit d'un cran.
//
// L'INTERFACE (R6) : une forme sombre (noir ou bleu profond, papier collé)
// derrière le titre de la pochette (mesuré dans le DOM, suit la pochette :
// effet de pochette, R19) et derrière le nom de la station en haut au centre.
//
// Debug : ?bauhauscomp=<n> (affiche fixe ; >= 16 : générée), ?bauhaustitle=<texte>,
// ?bauhausglyphs=1 (les lettres seules),
// ?bauhausmode=print|iris|glide|cut (transition forcée), ?bauhausprobe=1.

const MAX = 160;                          // lignes de la texture d'instances
const NO_BPM = 112;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const DROP_BEATS = 16;
const MODES = ['print', 'iris', 'glide', 'cut'];
const MODE_BEATS = { print: 4, iris: 2, glide: 2, cut: 2.5 };

// les encres (sRGB) ; A = la couleur de la pochette
const INKS = { Y: [0.949, 0.765, 0.110], R: [0.851, 0.290, 0.165], B: [0.118, 0.231, 0.584], K: [0.106, 0.098, 0.102], A: [0.24, 0.49, 0.42] };
const INK_KEYS = ['Y', 'R', 'B', 'K', 'A'];
const PRINT_ORDER = { Y: 0, R: 1, B: 2, A: 2, K: 3 };
const PAPER = [0.918, 0.882, 0.808];      // 0xeae1ce
const PAPER_BASE = 0.94;                  // le cran au-dessus (drop) : 1,0 — toujours sous 0,78 de luminance
const SCREEN_ANGLES = { Y: 0, R: 15, B: 75, K: 45, A: 30 };
const FAMILIES = ['kick', 'bass', 'snare', 'hats', 'melody', 'accent'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
export function softElastic(u) {
  const x = clamp01(u);
  return 1 - Math.pow(1 - x, 3) + 0.08 * Math.sin(x * Math.PI) * (1 - x) * 2.2;
}
function easeInOut(u) { const x = clamp01(u); return x * x * (3 - 2 * x); }
export function matchShapes(a, b) {
  const used = new Set(), out = [];
  for (const s of a) {
    let best = -1, bd = 1e9;
    b.forEach((t, j) => { if (used.has(j) || t.type !== s.type || (t.type === 'G') !== (s.type === 'G')) return; const d = Math.hypot(t.x - s.x, t.y - s.y); if (d < bd) { bd = d; best = j; } });
    if (best >= 0) { used.add(best); out.push({ from: s, to: b[best] }); } else out.push({ from: s, to: null });
  }
  b.forEach((t, j) => { if (!used.has(j)) out.push({ from: null, to: t }); });
  return out;
}
// le titre affiché : sans « (feat. …) », « - Remastered », crochets
export function titleText(s) {
  let t = String(s || '');
  t = t.split(/ [([]| - /)[0];
  return normaliseText(t).slice(0, 40);
}

// ------------------------------------------------------------------ GLSL
const FS_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// le papier, en sRGB, dans la cible privée
const PAPER_FRAGMENT = `
  precision highp float;
  uniform vec3 uPaper;
  uniform float uBase;
  uniform float uAspect;
  uniform vec2 uRes;
  varying vec2 vUv;
  float h12(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float vnoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h12(i), h12(i + vec2(1, 0)), f.x), mix(h12(i + vec2(0, 1)), h12(i + vec2(1, 1)), f.x), f.y); }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 px = vUv * uRes;
    float grain = (h12(floor(px)) - 0.5) * 0.034;
    vec2 fp = mat2(0.94, 0.34, -0.34, 0.94) * s * vec2(5.0, 140.0);
    float fib = smoothstep(0.80, 0.95, vnoise(fp)) * 0.035 - smoothstep(0.82, 0.97, vnoise(fp * 1.7 + 11.0)) * 0.020;
    // des taches de pâte, très basses fréquences : le papier n'est jamais un aplat
    float blot = (vnoise(s * 3.0 + 4.0) - 0.5) * 0.035;
    vec3 c = uPaper * uBase * (1.0 + grain - fib + blot);
    c *= 1.0 - 0.10 * smoothstep(0.6, 1.9, length(s / vec2(uAspect, 1.0) * vec2(1.4, 1.0)));
    gl_FragColor = vec4(c, 1.0);
  }
`;
// les formes : une ligne de la texture d'instances par forme (4 texels)
//   t0 x, y, w, h (unités de hauteur)   t1 rot, type, encre, niveau
//   t2 p1, p2, échelle, opacité         t3 glyphe (case x, case y, largeur) ou anim ; drapeaux
// uPass : 0 encres (multiplication), 1 ombres d'un niveau, 2 papiers d'un niveau
const SHAPE_VERTEX = `
  attribute float aIndex;
  uniform sampler2D uData;
  uniform float uCount;
  uniform float uAspect;
  uniform vec2 uRes;
  uniform float uPass;
  uniform float uLevel;
  uniform float uGlyphs;            // passe d'encre : 0 les formes, 1 les lettres (imprimées en dernier, par-dessus le collage)
  uniform vec2 uReg[5];
  varying vec2 vL;
  varying vec2 vW;
  varying vec4 vT1;
  varying vec4 vT2;
  varying vec4 vT3;
  varying vec2 vWH;
  vec4 fetch(float k) { return texture2D(uData, vec2((k + 0.5) / 4.0, (aIndex + 0.5) / ${MAX}.0)); }
  void main() {
    vec4 P = fetch(0.0), Q = fetch(1.0), R2 = fetch(2.0), T3 = fetch(3.0);
    float t = Q.y;
    bool ink = uPass < 0.5;
    bool glyph = t > 14.5;
    if (aIndex >= uCount || R2.z <= 0.001 || R2.w <= 0.001 || (ink ? (Q.w > 0.5 || glyph != (uGlyphs > 0.5)) : abs(Q.w - uLevel) > 0.5)) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec2 wh = P.zw * R2.z;
    float ppu = uRes.y * 0.5;
    float stroke = R2.x / ppu;
    // la demi-boîte de la forme dans son repère
    vec2 ext = wh * 0.5;
    if (t < 4.5 && t != 1.0) ext = vec2(wh.x * 0.5);
    if (t > 0.5 && t < 1.5) ext = vec2(wh.x * 0.5 + stroke);
    if (t > 4.5 && t < 5.5) ext = vec2(wh.x);                  // quart de disque : le coin au centre
    if (t > 7.5 && t < 8.5) ext = vec2(wh.x * 0.5);            // carré
    if (t > 8.5 && t < 9.5) ext = vec2(wh.x * 0.5, stroke);    // filet
    if (t > 9.5 && t < 10.5) ext = vec2(wh.x * 0.5 + wh.y * 0.5, wh.y * 0.5);
    if (t > 10.5 && t < 11.5) ext = vec2(wh.x * 0.5);
    if (t > 12.5 && t < 13.5) ext = vec2(wh.x * 0.5);
    if (t > 13.5 && t < 14.5) ext = vec2(wh.x * 0.5, wh.x * 0.18 + stroke);
    float padPx = uPass > 1.5 ? 2.0 : (uPass > 0.5 ? 9.0 : 3.0);
    vec2 lp = position.xy * (ext + padPx / ppu);
    float c = cos(Q.x), s = sin(Q.x);
    vec2 w = P.xy + mat2(c, s, -s, c) * lp;
    if (ink) {
      int k = int(Q.z + 0.5);
      vec2 reg = uReg[0];
      if (k == 1) reg = uReg[1]; else if (k == 2) reg = uReg[2]; else if (k == 3) reg = uReg[3]; else if (k == 4) reg = uReg[4];
      w += reg / ppu;
    }
    // l'ombre portée : 3 px vers le bas à droite
    if (uPass > 0.5 && uPass < 1.5) { w += vec2(3.0, -3.0) / ppu; }
    vL = lp; vW = w; vT1 = Q; vT2 = R2; vT3 = T3; vWH = wh;
    gl_Position = vec4(w.x / uAspect, w.y, 0.0, 1.0);
  }
`;
const SHAPE_FRAGMENT = `
  precision highp float;
  uniform vec3 uInk[5];
  uniform float uScreen[5];
  uniform vec2 uRes;
  uniform float uPass;
  uniform sampler2D uAtlas;
  uniform vec2 uAtlasSize;
  uniform vec4 uAtlasCell;          // case, capitale, marge (px d'atlas), étalement
  uniform vec4 uSweep;              // PRINT : x de la passe de chaque encre (Y, R, B, K), unités
  uniform float uPrintOn;
  uniform vec4 uReserve;            // la case de la pochette (centre, demi-taille), unités
  uniform vec3 uPaper;
  varying vec2 vL;
  varying vec2 vW;
  varying vec4 vT1;
  varying vec4 vT2;
  varying vec4 vT3;
  varying vec2 vWH;
  float sdBox(vec2 p, vec2 b) { vec2 q = abs(p) - b; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0); }
  float sdPie(vec2 p, vec2 c, float r) { p.x = abs(p.x); float l = length(p) - r; float m = length(p - c * clamp(dot(p, c), 0.0, r)); return max(l, m * sign(c.y * p.x - c.x * p.y)); }
  float sdArc(vec2 p, vec2 sc, float ra, float rb) { p.x = abs(p.x); return ((sc.y * p.x > sc.x * p.y) ? length(p - sc * ra) : abs(length(p) - ra)) - rb; }
  void main() {
    float t = vT1.y;
    vec2 p = vL, wh = vWH;
    float ppu = uRes.y * 0.5, stroke = vT2.x / ppu * 0.5;
    float R = wh.x * 0.5, r = length(p);
    float d = 1.0, lit = 1.0;
    if (t < 0.5) d = r - R;
    else if (t < 1.5) d = abs(r - R) - stroke;
    else if (t < 2.5) { float n = max(vT2.x, 1.0); float st = R / n; float k = clamp(floor(r / st + 0.5), 1.0, n); d = max(abs(r - k * st) - 1.3 / ppu, r - R - 1.5 / ppu); }
    else if (t < 3.5) { float h = vT2.y * 0.5; d = sdPie(p, vec2(sin(h), cos(h)), R); }
    else if (t < 4.5) { float h = vT2.y * 0.5; d = sdArc(p, vec2(sin(h), cos(h)), R, max(stroke, 0.75 / ppu)); }
    else if (t < 5.5) d = max(r - wh.x, max(-p.x, -p.y));
    else if (t < 6.5) d = sdBox(p, wh * 0.5);
    else if (t < 7.5) { float hw = wh.x * 0.5, hh = wh.y * 0.5; float nl = length(vec2(wh.y, hw)); d = max(-p.y - hh, (abs(p.x) * wh.y + (p.y - hh) * hw) / nl); }
    else if (t < 8.5) d = sdBox(p, vec2(wh.x * 0.5));
    else if (t < 9.5) d = sdBox(p, vec2(wh.x * 0.5, max(stroke, 0.5 / ppu)));
    else if (t < 10.5) {
      float n = max(vT2.x, 2.0), st = wh.x / (n - 1.0);
      float k = clamp(floor((p.x + wh.x * 0.5) / st + 0.5), 0.0, n - 1.0);
      d = length(p - vec2(-wh.x * 0.5 + k * st, 0.0)) - wh.y * 0.5;
      // la rangée de charleys : les points allumés jusqu'à vT3.x, les autres à peine imprimés
      lit = k < vT3.x ? 1.0 : 0.22;
    }
    else if (t < 11.5) {
      float cell = wh.x * 0.25; vec2 g = (p + wh.x * 0.5) / cell; vec2 f = fract(g) - 0.5;
      float par = mod(floor(g.x) + floor(g.y), 2.0);
      float m = min(0.5 - abs(f.x), 0.5 - abs(f.y)) * cell;
      d = max(sdBox(p, vec2(wh.x * 0.5)), par < 0.5 ? -m : m);
    }
    else if (t < 12.5) { float st = wh.x / max(vT2.x, 1.0); float f = mod(p.x + wh.x * 0.5, st) - st * 0.5; d = max(sdBox(p, wh * 0.5), abs(f) - 0.8 / ppu); }
    else if (t < 13.5) d = min(sdBox(p, vec2(wh.x * 0.5, stroke)), sdBox(p, vec2(stroke, wh.x * 0.5)));
    else if (t < 14.5) {
      float hl = wh.x * 0.28, hw = wh.x * 0.18;
      float shaft = sdBox(p - vec2(-hl * 0.5, 0.0), vec2(wh.x * 0.5 - hl * 0.5, stroke));
      float head = max(-(p.x - (wh.x * 0.5 - hl)), (abs(p.y) * hl + (p.x - wh.x * 0.5) * hw) / length(vec2(hl, hw)));
      d = min(shaft, head);
    }
    else {
      // la lettre : l'atlas de champs de distance (unités de capitale -> unités écran)
      vec2 gp = p / wh.y + vec2(vT3.z * 0.5, 0.5);
      vec2 apx = vT3.xy * uAtlasCell.x + vec2((uAtlasCell.x - vT3.z * uAtlasCell.y) * 0.5, uAtlasCell.z) + gp * uAtlasCell.y;
      float v = texture2D(uAtlas, apx / uAtlasSize).r;
      d = (0.5 - v) * 2.0 * uAtlasCell.w / uAtlasCell.y * wh.y;
    }
    // aucune forme dans la case de la pochette (les anneaux, les barres qui glissent, les lettres dispersées)
    vec2 rq = abs(vW - uReserve.xy) - uReserve.zw;
    bool anchor = vT3.w > 0.5;
    if (!anchor && rq.x < 0.0 && rq.y < 0.0) discard;
    // PRINT : chaque encre n'existe qu'à gauche de sa passe
    if (uPrintOn > 0.5 && !anchor) {
      int k = int(vT1.z + 0.5);
      float sw = k == 0 ? uSweep.x : (k == 1 ? uSweep.y : (k == 3 ? uSweep.w : uSweep.z));
      if (vW.x > sw) discard;
    }
    float dpx = d * ppu;
    int k = int(vT1.z + 0.5);
    vec3 ink = uInk[0]; float ang = uScreen[0];
    if (k == 1) { ink = uInk[1]; ang = uScreen[1]; } else if (k == 2) { ink = uInk[2]; ang = uScreen[2]; } else if (k == 3) { ink = uInk[3]; ang = uScreen[3]; } else if (k == 4) { ink = uInk[4]; ang = uScreen[4]; }
    float op = vT2.w;
    if (uPass > 0.5 && uPass < 1.5) {
      // l'ombre portée, douce (3 px), multipliée
      float sh = (1.0 - smoothstep(-3.0, 3.0, dpx)) * 0.26 * op;
      if (sh <= 0.002) discard;
      gl_FragColor = vec4(vec3(1.0 - sh), 1.0);
      return;
    }
    float a = clamp(0.5 - dpx, 0.0, 1.0) * op * lit;
    if (a <= 0.0) discard;
    if (uPass > 1.5) {
      // un papier découpé, imprimé dans la masse : un liseré clair d'un pixel (la tranche)
      vec3 c = ink * (1.0 + 0.10 * (1.0 - smoothstep(0.0, 1.5, -dpx)));
      gl_FragColor = vec4(c, a);
      return;
    }
    // l'encre : une transmittance qui multiplie ce qui est dessous
    vec3 T = clamp(ink / uPaper, 0.0, 1.0);
    // plus dense sur les bords (l'encre s'accumule au bord de la plaque)
    T *= 1.0 - 0.10 * (1.0 - smoothstep(0.0, 3.0, -dpx));
    // la trame : des points de 4,5 px à l'angle de la plaque, visibles de près
    float ca = cos(ang), sa = sin(ang);
    vec2 f = fract(mat2(ca, sa, -sa, ca) * gl_FragCoord.xy / 4.5) - 0.5;
    float dotm = 1.0 - smoothstep(0.30, 0.44, length(f));
    T = mix(T, vec3(1.0), 0.07 * (1.0 - dotm));
    gl_FragColor = vec4(mix(vec3(1.0), T, a), 1.0);
  }
`;
// l'affichage : la cible courante (et l'ancienne affiche pendant PRINT, IRIS, CUT), sRGB -> linéaire
const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D uCur;
  uniform sampler2D uSnap;
  uniform float uMode;              // 0 rien, 1 PRINT, 2 IRIS, 3 CUT
  uniform vec4 uA;                  // PRINT : x de la passe jaune, x de la passe active ; IRIS : centre, rayon ; CUT : point, direction
  uniform vec4 uB;                  // CUT : décalage de la moitié + (xy), de la moitié - (zw)
  uniform float uAspect;
  uniform vec2 uRes;
  varying vec2 vUv;
  vec3 toLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c)); }
  vec2 toUv(vec2 s) { return vec2(s.x / uAspect, s.y) * 0.5 + 0.5; }
  bool onScreen(vec2 uv) { return uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0; }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float ppu = uRes.y * 0.5;
    vec3 c = texture2D(uCur, vUv).rgb;
    if (uMode > 0.5 && uMode < 1.5) {
      // PRINT : à droite de la passe jaune, l'ancienne affiche ; le rouleau : une ombre douce devant la passe active
      if (s.x > uA.x) c = texture2D(uSnap, vUv).rgb;
      float ahead = s.x - uA.y;
      c *= 1.0 - 0.10 * exp(-max(ahead, 0.0) * ppu / 10.0) * step(0.0, ahead) * step(uA.y, uAspect + 0.05);
    } else if (uMode > 1.5 && uMode < 2.5) {
      float dd = (length(s - uA.xy) - uA.z) * ppu;
      if (dd > 0.0) c = texture2D(uSnap, vUv).rgb;
      // le bord de l'iris : un filet noir de 3 px
      c *= 1.0 - 0.85 * (1.0 - smoothstep(1.0, 2.5, abs(dd))) * step(0.001, uA.z);
    } else if (uMode > 2.5) {
      vec2 dir = uA.zw, n = vec2(-dir.y, dir.x);
      // la moitié + : on lit l'ancienne affiche au point d'origine
      vec2 qa = s - uB.xy, qb = s - uB.zw;
      float sa = dot(qa - uA.xy, n), sb = dot(qb - uA.xy, n);
      vec2 ua = toUv(qa), ub = toUv(qb);
      // les ombres des deux moitiés sur la nouvelle affiche (3 px vers le bas à droite)
      vec2 o = vec2(-4.0, 4.0) / ppu;
      float sha = dot(qa + o - uA.xy, n) * ppu, shb = dot(qb + o - uA.xy, n) * ppu;
      float shadow = max(onScreen(toUv(qa + o)) ? 1.0 - smoothstep(-6.0, 6.0, -sha) : 0.0, onScreen(toUv(qb + o)) ? 1.0 - smoothstep(-6.0, 6.0, shb) : 0.0);
      c *= 1.0 - 0.28 * shadow;
      if (sa > 0.0 && onScreen(ua)) { c = texture2D(uSnap, ua).rgb; c *= 1.0 - 0.35 * (1.0 - smoothstep(0.0, 2.0, sa * ppu)); }
      else if (sb < 0.0 && onScreen(ub)) { c = texture2D(uSnap, ub).rgb; c *= 1.0 - 0.35 * (1.0 - smoothstep(0.0, 2.0, -sb * ppu)); }
    }
    gl_FragColor = vec4(toLinear(c), 1.0);
  }
`;

// =============================================================================

export class BauhausVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    const n = parseInt(urlParam('bauhauscomp'), 10);
    this._forceComp = Number.isFinite(n) && n >= 0 ? n : null;
    this._forceTitle = urlParam('bauhaustitle');
    this._onlyGlyphs = urlParam('bauhausglyphs') === '1';
    const fm = urlParam('bauhausmode');
    this._forceMode = MODES.includes(fm) ? fm : null;
    // mat : aucun bloom, aucune lueur ; la vignette est celle du papier
    this._post = { bloomStrength: 0, bloomThreshold: 1, grain: 0, vignette: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('bauhausprobe') === '1' && typeof window !== 'undefined') window.__bauhaus = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color().setRGB(PAPER[0], PAPER[1], PAPER[2], THREE.SRGBColorSpace);
    const opts = { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false };
    this._rt = [new THREE.WebGLRenderTarget(16, 16, opts), new THREE.WebGLRenderTarget(16, 16, opts)];
    this._cur = 0;
    this._res = new THREE.Vector2(1280, 720);

    // la scène privée : papier, encres, ombres et papiers des deux niveaux
    this._pscene = new THREE.Scene();
    this._pcam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._PU = { uPaper: { value: new THREE.Vector3(...PAPER) }, uBase: { value: PAPER_BASE }, uAspect: safeZoneUniforms.uAspect, uRes: { value: this._res } };
    const paper = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._PU, vertexShader: FS_VERTEX, fragmentShader: PAPER_FRAGMENT, depthTest: false, depthWrite: false }));
    paper.frustumCulled = false; paper.renderOrder = 0;
    this._pscene.add(paper);
    this._paper = paper;

    this._data = new Float32Array(4 * 4 * MAX);
    this._dataTex = new THREE.DataTexture(this._data, 4, MAX, THREE.RGBAFormat, THREE.FloatType);
    this._dataTex.minFilter = this._dataTex.magFilter = THREE.NearestFilter;
    this._dataTex.needsUpdate = true;
    const atlas = buildAtlas();
    this._atlasTex = new THREE.DataTexture(atlas.data, atlas.width, atlas.height, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._atlasTex.minFilter = this._atlasTex.magFilter = THREE.LinearFilter;
    this._atlasTex.needsUpdate = true;
    const [aw, ah] = atlasSize();
    this._SU = {
      uData: { value: this._dataTex }, uCount: { value: 0 }, uAspect: safeZoneUniforms.uAspect, uRes: { value: this._res },
      uReg: { value: INK_KEYS.map(() => new THREE.Vector2()) },
      uInk: { value: INK_KEYS.map((k) => new THREE.Vector3(...INKS[k])) },
      uScreen: { value: INK_KEYS.map((k) => SCREEN_ANGLES[k] * Math.PI / 180) },
      uAtlas: { value: this._atlasTex }, uAtlasSize: { value: new THREE.Vector2(aw, ah) },
      uAtlasCell: { value: new THREE.Vector4(ATLAS.CELL, ATLAS.CAP, (ATLAS.CELL - ATLAS.CAP) / 2, ATLAS.SPREAD) },
      uSweep: { value: new THREE.Vector4(9, 9, 9, 9) }, uPrintOn: { value: 0 },
      uReserve: { value: new THREE.Vector4(0, 0, RESERVE[0] * COL, RESERVE[1] * COL) },
      uPaper: this._PU.uPaper
    };
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const idx = new Float32Array(MAX); for (let i = 0; i < MAX; i++) idx[i] = i;
    g.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1));
    g.instanceCount = MAX;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._geo = g;
    const multiply = { blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.DstColorFactor, blendDst: THREE.ZeroFactor, blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor };
    const pass = (passId, level, order, blend, glyphs) => {
      const U = Object.assign({}, this._SU, { uPass: { value: passId }, uLevel: { value: level }, uGlyphs: { value: glyphs ? 1 : 0 } });
      const m = new THREE.Mesh(g, new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: SHAPE_VERTEX, fragmentShader: SHAPE_FRAGMENT, depthTest: false, depthWrite: false, transparent: true }, blend)));
      m.frustumCulled = false; m.renderOrder = order;
      this._pscene.add(m);
      return m;
    };
    this._passes = [
      pass(0, 0, 1, multiply),
      pass(1, 1, 2, multiply), pass(2, 1, 3, { blending: THREE.NormalBlending }),
      pass(1, 2, 4, multiply), pass(2, 2, 5, { blending: THREE.NormalBlending }),
      pass(0, 0, 6, multiply, true)
    ];

    // l'affichage, dans la scène partagée
    this._DU = {
      uCur: { value: this._rt[0].texture }, uSnap: { value: this._rt[1].texture }, uMode: { value: 0 },
      uA: { value: new THREE.Vector4() }, uB: { value: new THREE.Vector4() }, uAspect: safeZoneUniforms.uAspect, uRes: { value: this._res }
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._DU, vertexShader: FS_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -2000;
    this._display.onBeforeRender = (renderer) => this._renderPoster(renderer);
    scene.add(this._display);

    // l'état
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._r = rng(2024);
    this._phraseN = 0; this._modeN = 0;
    this._lowS = 0.5; this._midS = 0.5;
    this._title = null; this._artist = '';
    this._compIdx = this._forceComp != null ? this._forceComp : 0;
    this._live = []; this._glyphRev = 0;
    this._trans = null; this._drop = null; this._lastMode = null; this._flash = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteKey = undefined; this._hasCover = false;
    this._anchors = { text: null, station: null, at: -1e9 };
    this._stats = { beats: 0, posters: 0, drops: 0, rings: 0, modes: { print: 0, iris: 0, glide: 0, cut: 0 } };
    this._pendingSnap = false; this._needRender = true;
    this._setPoster(this._compIdx, null);
  }

  // ------------------------------------------------------------ l'affiche
  _posterDef(i) { return i < POSTERS.length ? POSTERS[i] : generatePoster(i); }

  // la liste d'éléments vivants d'une affiche (formes + lettres du titre)
  _build(i) {
    const def = this._posterDef(i);
    const r = rng(7001 + i * 13);
    // la couleur de la pochette pour une famille, choisie par phrase
    const famA = FAMILIES[(this._phraseN + i) % FAMILIES.length];
    const els = def.shapes.map((s, k) => {
      const fam = familyOf(s);
      let ink = s.ink;
      if (fam === 'kick') ink = 'B'; else if (fam === 'snare') ink = 'Y'; else if (fam === 'accent') ink = 'R';
      if (fam && fam === famA) ink = 'A';
      const p1 = s.type === 'C' || s.type === 'Q' || s.type === 'B' || s.type === 'S' || s.type === 'T' ? 0 : s.p1;
      return { id: 's' + k, kind: 'shape', type: s.type, fam, micro: isMicro(s), base: Object.assign({}, s, { ink, p1 }), group: k % 2, rotAdd: 0, rotFrom: 0, rotAt: -99 };
    });
    // le titre
    this._builtAspect = safeZoneUniforms.uAspect.value || 1;
    const halfW = this._builtAspect / COL;
    const text = this._forceTitle != null ? normaliseText(this._forceTitle) : (i % 4 === 3 && this._artist ? this._artist : this._title) || 'BAUHAUS';
    const glyphs = layoutPosterTitle(text, def.title, glyphWidth, Math.min(halfW, SCREEN[0] + 1));
    glyphs.slice(0, 32).forEach((gl, k) => {
      const cell = glyphCell(gl.ch);
      if (!cell) return;
      els.push({ id: 'g' + k, kind: 'glyph', type: 'G', fam: null, micro: false, cell, base: { type: 'G', ch: gl.ch, x: gl.x, y: gl.y, w: gl.w, h: gl.h, rot: gl.rot, ink: gl.initial ? (def.title.ink === 'K' ? 'R' : 'K') : def.title.ink, level: 0, p1: 0, p2: 0, z: 1000 + k }, initial: !!gl.initial, rnd: [r() * 2 - 1, r() * 2 - 1, r() * 2 - 1] });
    });
    for (const e of els) e.cur = Object.assign({ scale: 1, opacity: 1 }, e.base);
    // le repérage : 1 à 2 px par plaque, propre à l'affiche (le noir est la référence)
    const reg = INK_KEYS.map((k) => { if (k === 'K') return [0, 0]; const a = r() * Math.PI * 2, m = 1 + r(); return [Math.cos(a) * m, Math.sin(a) * m]; });
    return { def, els, reg, famA, text };
  }

  _setPoster(i, mode) {
    const clock = this._clock;
    const next = this._build(i);
    this._stats.posters++;
    next.reg.forEach((v, k) => this._SU.uReg.value[k].set(v[0], v[1]));
    if (mode === 'glide' && this._live.length) {
      // GLIDE : appariement par type ; les orphelines rétrécissent, les nouvelles grandissent
      const now = this._live.filter((e) => !e.dying && e.kind !== 'ring');
      const pairs = matchShapes(now.map((e) => Object.assign({}, e.cur, { _e: e })), next.els.map((e) => Object.assign({}, e.base, { _e: e })));
      const live = [];
      for (const { from, to } of pairs) {
        if (from && to) {
          const e = to._e, o = from._e;
          e.cur = Object.assign({}, o.cur);
          e.glide = { from: Object.assign({}, o.cur), start: clock + (e.kind === 'glyph' ? 0.04 * live.length : 0), dur: 2 };
          live.push(e);
        } else if (from) {
          const o = from._e; o.dying = { start: clock, dur: 1, from: Object.assign({}, o.cur) }; live.push(o);
        } else {
          const e = to._e; e.cur.scale = 0; e.glide = { from: Object.assign({}, e.base, { scale: 0 }), start: clock + 0.5 + 0.03 * live.length, dur: 1.5 }; live.push(e);
        }
      }
      this._live = live;
    } else {
      this._live = next.els;
    }
    this._posterNow = next;
    this._needRender = true;
  }

  // ------------------------------------------------------------ couleurs, drop
  _applyPalette(pal) {
    const v = pal && (pal.vividColors || pal.dominantColors);
    const key = v && v.length ? String(v[0]) : '';
    if (key === this._paletteKey) return;
    this._paletteKey = key; this._hasCover = !!key;
    // la couleur de la pochette, ramenée à une encre franche et mate (jamais pastel)
    // (teinte, saturation et clarté lues et posées en sRGB : l'encre est en sRGB)
    const c = new THREE.Color(), hsl = {}, out = {};
    if (key) { c.set(v[0]); c.getHSL(hsl, THREE.SRGBColorSpace); } else { hsl.h = 0.45; hsl.s = 0.5; hsl.l = 0.38; }
    c.setHSL(hsl.h, THREE.MathUtils.clamp(hsl.s, 0.55, 0.80), THREE.MathUtils.clamp(hsl.l * 0.75, 0.30, 0.42), THREE.SRGBColorSpace);
    c.getRGB(out, THREE.SRGBColorSpace);
    this._SU.uInk.value[4].set(out.r, out.g, out.b);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  _readTitle(profile) {
    const t = titleText((profile.currentTrack && profile.currentTrack.title) || profile.title || '');
    const a = titleText(profile.artist || (profile.currentTrack && profile.currentTrack.artist) || '');
    if (t !== this._title) {
      const first = this._title === null;
      this._title = t; this._artist = a;
      // un nouveau morceau : l'affiche garde sa structure, le titre change (sans transition au premier)
      if (!first || this._live.some((e) => e.kind === 'glyph')) this._setPoster(this._compIdx, first ? null : 'glide');
    }
  }

  // LES FORMES SOMBRES de l'interface (R6) : mesurées dans le DOM deux fois par seconde
  _measureAnchors(renderer) {
    if (this._time - this._anchors.at < 0.5 && this._anchors.text) return;
    this._anchors.at = this._time;
    const canvas = renderer && renderer.domElement;
    const doc = typeof document !== 'undefined' ? document : null;
    const cr = canvas && canvas.getBoundingClientRect ? canvas.getBoundingClientRect() : null;
    const toUnits = (r) => {
      if (!cr || !cr.height) return null;
      const s = 2 / cr.height;
      return { x: (r.left + r.width / 2 - cr.left - cr.width / 2) * s, y: (cr.top + cr.height / 2 - r.top - r.height / 2) * s, hw: r.width / 2 * s, hh: r.height / 2 * s };
    };
    const rectOf = (ids) => {
      if (!doc) return null;
      let box = null;
      for (const id of ids) {
        const el = doc.getElementById(id);
        if (!el || !el.textContent || !el.textContent.trim()) continue;
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) continue;
        const st = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
        if (st && (st.visibility === 'hidden' || st.display === 'none' || +st.opacity === 0)) continue;
        box = box ? { left: Math.min(box.left, r.left), top: Math.min(box.top, r.top), right: Math.max(box.right, r.right), bottom: Math.max(box.bottom, r.bottom) } : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
      }
      return box ? Object.assign(box, { width: box.right - box.left, height: box.bottom - box.top }) : null;
    };
    let text = rectOf(['overlay-title', 'overlay-artist']);
    text = text ? toUnits(text) : null;
    if (!text) {
      // repli : sous la pochette (le bas du bloc mesuré)
      const sc = safeZoneUniforms.uSafeCenter.value, sh = safeZoneUniforms.uSafeHalfSize.value;
      const th = Math.max(0.06, sh.y - sh.x);
      text = { x: sc.x, y: sc.y - sh.y + th / 2, hw: sh.x * 0.9, hh: th / 2 };
    }
    let st = rectOf(['ext-station-name']);
    st = st ? toUnits(st) : null;
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    if (!st || st.y < 0.6) st = { x: 0, y: 1 - 0.05, hw: 0.26 * A, hh: 0.045 };
    this._anchors.text = text; this._anchors.station = st;
  }

  // ------------------------------------------------------------ la boucle
  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._applyPalette(profile.artworkPalette);
    this._readTitle(profile);
    // l'aspect connu après init (ou changé) : le titre se recompose dans la colonne réelle
    const asp = safeZoneUniforms.uAspect.value || 1;
    if (this._builtAspect != null && Math.abs(asp - this._builtAspect) > 0.01) this._setPoster(this._compIdx, null);
    if (profile.playbackState !== 'PLAYING') { this._paused = true; this._write(this._clock, profile); return; }   // pause : tout gèle
    this._paused = false;
    this._time += dt;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock;
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    // les bandes, lissées ; sans analyse, une basse et une mélodie synthétiques
    const director = profile.director;
    const bands = director && director.bands;
    const real = bands && (bands.low > 0 || bands.mid > 0);
    const low = real ? bands.low : 0.5 + 0.35 * Math.sin(clock * Math.PI / 4);
    const mid = real ? bands.mid : 0.5 + 0.4 * Math.sin(clock * Math.PI / 8 + 1.3);
    this._lowS += (low - this._lowS) * (1 - Math.exp(-dt / 0.15));
    this._midS += (mid - this._midS) * (1 - Math.exp(-dt / 0.20));

    // DROP
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && !this._drop) { this._drop = { start: clock }; this._flash = 1; this._stats.drops++; }
    if (this._drop && clock - this._drop.start >= DROP_BEATS) this._drop = null;
    this._flash *= Math.exp(-dt / 0.6);

    // PHRASE : une nouvelle affiche, transitions en alternance (jamais pendant le drop)
    if (this._trans && clock - this._trans.start >= this._trans.dur) { this._trans = null; this._DU.uMode.value = 0; this._SU.uPrintOn.value = 0; }
    if (phrase && !this._drop && !this._trans) {
      this._phraseN++;
      if (this._forceComp == null) this._compIdx++;
      const mode = this._forceMode || MODES[this._modeN++ % MODES.length];
      this._startTransition(mode, clock);
    }
    if (beat) {
      this._stats.beats++;
      // KICK : un anneau se détache d'un des grands cercles
      const kicks = this._live.filter((e) => e.fam === 'kick' && !e.dying);
      if (kicks.length) {
        const src = kicks[Math.floor(clock) % kicks.length];
        this._live.push({ id: 'ring' + Math.floor(clock), kind: 'ring', type: 'R', fam: null, src, born: Math.floor(clock), base: Object.assign({}, src.base, { type: 'R', ink: src.base.ink === 'A' ? 'A' : 'K', level: 0, p1: 2, z: 999 }), cur: Object.assign({}, src.cur, { type: 'R', p1: 2, z: 999 }) });
        this._stats.rings++;
      }
      // CAISSE : les triangles pivotent de 30° sur les contretemps
      if (Math.floor(clock) % 2 === 1) for (const e of this._live) if (e.fam === 'snare') { e.rotFrom = e.rotAdd; e.rotAdd += 30; e.rotAt = Math.floor(clock); }
    }
    if (bar) {
      // TEMPS FORT : une lettre glisse ou pivote d'un quart de tour, puis revient
      const gl = this._live.filter((e) => e.kind === 'glyph' && !e.dying);
      if (gl.length) { const e = gl[Math.floor(this._r() * gl.length)]; e.jolt = { start: Math.floor(clock), kind: this._r() < 0.5 ? 'slide' : 'turn' }; }
    }
    this._live = this._live.filter((e) => e.kind !== 'ring' || clock - e.born < 1.6);
    this._write(clock, profile);
  }

  _startTransition(mode, clock) {
    this._stats.modes[mode]++;
    if (mode === 'glide') { this._setPoster(this._compIdx, 'glide'); this._trans = { mode, start: clock, dur: MODE_BEATS.glide }; return; }
    // l'ancienne affiche reste dans la cible courante, qui devient l'instantané (échange, sans copie)
    const old = this._live.filter((e) => e.fam === 'kick' && !e.dying);
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    const t = { mode, start: clock, dur: MODE_BEATS[mode] };
    if (mode === 'iris') {
      const src = old.length ? old[0].cur : { x: -4, y: 0 };
      const c = this._toUnits(src.x, src.y);
      t.center = c; t.rmax = Math.hypot(A + Math.abs(c.x), 1 + Math.abs(c.y)) + 0.05;
      t.r0 = old.length ? old[0].cur.w * COL * 0.5 : 0.05;
    } else if (mode === 'cut') {
      const ang = (this._r() < 0.5 ? 1 : -1) * (28 + this._r() * 14) * Math.PI / 180;
      t.dir = [Math.sin(ang), Math.cos(ang)];               // une diagonale proche de la verticale
      t.point = [(this._r() - 0.5) * 0.3, (this._r() - 0.5) * 0.3];
      t.reach = 2 * A + 0.5;
    }
    this._trans = t;
    this._cur ^= 1;
    this._setPoster(this._compIdx, null);
  }

  _toUnits(x, y) { const sc = safeZoneUniforms.uStageCenter.value; return { x: sc.x + x * COL, y: sc.y + y * COL }; }

  // ------------------------------------------------------------ l'écriture
  _write(clock, profile) {
    const drop = this._drop ? clamp01((clock - this._drop.start) / DROP_BEATS) : null;
    const amp = this._drop ? 1 : 0.7;
    const spb = 60 / this._bpm;
    const tb = (clock - Math.floor(clock)) * spb;                  // secondes depuis le temps
    const kickEnv = tb < 0.08 ? tb / 0.08 : 1 - easeInOut((tb - 0.08) / 0.25);
    const e8 = Math.floor(clock * 2);
    const barPh = clock - 4 * Math.floor(clock / 4);
    const barN = Math.floor(clock / 4);
    const t = this._time;

    // les glissements et les disparitions (GLIDE)
    for (const e of this._live) {
      if (e.glide) {
        const u = clamp01((clock - e.glide.start) / e.glide.dur), k = softElastic(u);
        for (const key of ['x', 'y', 'w', 'h', 'rot', 'scale']) e.cur[key] = e.glide.from[key] + ((key === 'scale' ? 1 : e.base[key]) - e.glide.from[key]) * k;
        e.cur.ink = u < 0.5 ? e.glide.from.ink : e.base.ink;
        if (u >= 1) { Object.assign(e.cur, e.base, { scale: 1 }); e.glide = null; }
      }
      if (e.dying) { const u = clamp01((clock - e.dying.start) / e.dying.dur); e.cur.scale = e.dying.from.scale * (1 - u * u); }
    }
    this._live = this._live.filter((e) => !(e.dying && clock - e.dying.start >= e.dying.dur));

    const rows = [];
    const levelPar = [0.02, 0.06, 0.10];
    const fs = this._famState = { kick: null, bass: null, snare: null, hats: null, melody: null, accent: null, glyph0: null };
    for (const e of this._live) {
      if (this._onlyGlyphs && e.kind !== 'glyph') continue;
      const c = e.cur, b = e.base;
      const settled = !e.glide && !e.dying;
      let x = c.x, y = c.y, w = c.w, h = c.h, rot = c.rot, scale = c.scale, opacity = 1, p2 = c.p2 || 0, anim = 0, along = 0;
      // la parallaxe : trois profondeurs
      const lv = Math.min(2, b.level || 0);
      x += levelPar[lv] * Math.sin(t * 0.21 + lv * 1.7); y += levelPar[lv] * Math.cos(t * 0.17 + lv);
      if (settled) {
        switch (e.fam) {
          case 'kick': scale *= 1 + (this._drop ? 0.20 : 0.12) * kickEnv; break;
          case 'bass': {
            const a = rot * Math.PI / 180; along = (this._lowS - 0.5) * 2 * 0.55 * amp / 0.7;
            const ax = w >= h ? Math.cos(a) : -Math.sin(a), ay = w >= h ? Math.sin(a) : Math.cos(a);
            x += ax * along; y += ay * along; break;
          }
          case 'snare': {
            const u = clamp01((clock - e.rotAt) / 0.35);
            rot += e.rotFrom + (e.rotAdd - e.rotFrom) * softElastic(u);
            if (this._drop) rot += (clock - this._drop.start) * 45;
            break;
          }
          case 'hats': anim = (e8 % ((c.p1 || 6) + 2)) + 1; break;
          case 'melody': {
            const m = clamp01(this._midS * 1.15);
            p2 = (b.p2 || Math.PI) * (1 - (this._drop ? 0.55 : 0.45) * (1 - m));
            break;
          }
          case 'accent': {
            const active = this._drop || e.group === barN % 2;
            if (!active) { scale = 0; break; }
            const pop = barPh < 0.3 ? softElastic(barPh / 0.3) : (barPh > 2.5 ? 1 - easeInOut((barPh - 2.5) / 0.5) : 1);
            scale *= Math.max(0, pop);
            rot += 90 * softElastic(clamp01(barPh));
            break;
          }
        }
      }
      // (la sonde : la valeur jouée par le premier élément de chaque famille)
      if (e.fam && settled && fs[e.fam] == null) fs[e.fam] = e.fam === 'kick' ? scale : e.fam === 'bass' ? along : e.fam === 'snare' ? rot - c.rot : e.fam === 'hats' ? anim : e.fam === 'melody' ? p2 : scale;
      if (e.kind === 'ring') {
        const u = clamp01((clock - e.born) / 1.5);
        const src = e.src.cur;
        x = src.x + levelPar[0] * Math.sin(t * 0.21); y = src.y + levelPar[0] * Math.cos(t * 0.17);
        w = h = src.w * (1.0 + 0.9 * (1 - Math.pow(1 - u, 2)));
        opacity = (1 - u) * (this._drop ? 1 : 0.8);
        scale = 1;
      }
      if (e.kind === 'glyph') {
        if (e.jolt && settled) {
          const u = clock - e.jolt.start;
          const k = u < 0.25 ? softElastic(u / 0.25) : (u < 1.25 ? 1 : 1 - softElastic((u - 1.25) / 0.4));
          if (u > 1.7) e.jolt = null;
          else if (e.jolt.kind === 'turn') rot += 90 * k;
          else { const a = rot * Math.PI / 180; x += Math.cos(a) * 0.35 * h * k; y += Math.sin(a) * 0.35 * h * k; }
        }
        // le drop : les lettres se dispersent (1 temps) puis se recomposent (3 temps)
        if (this._drop && settled) {
          const u = clock - this._drop.start;
          const k = u < 1 ? 1 - Math.pow(1 - u, 3) : (u < 4 ? 1 - softElastic((u - 1) / 3) : 0);
          x += e.rnd[0] * 3.2 * k; y += e.rnd[1] * 2.2 * k; rot += e.rnd[2] * 200 * k;
        }
        if (fs.glyph0 == null) fs.glyph0 = Math.hypot(x - c.x, y - c.y);
      }
      const u = this._toUnits(x, y);
      rows.push({ z: (b.level || 0) * 10000 + (b.z || 0), v: [u.x, u.y, w * COL, h * COL, rot * Math.PI / 180, TYPES[c.type], INK_KEYS.indexOf(c.ink), b.level || 0, c.p1 || 0, p2, Math.max(0, scale), opacity,
        e.kind === 'glyph' ? e.cell.cx : anim, e.kind === 'glyph' ? e.cell.cy : 0, e.kind === 'glyph' ? e.cell.w : 0, 0] });
    }
    // les formes sombres de l'interface (papiers collés au niveau le plus haut, jamais coupées)
    const an = this._anchors;
    if (an.text) {
      const T = an.text, padX = 0.05, padY = 0.03;
      rows.push({ z: 99990, v: [T.x, T.y, 2 * (T.hw + padX), 2 * (T.hh + padY), 0, TYPES.B, (this._compIdx % 3 === 1) ? 2 : 3, 2, 0, 0, 1, 1, 0, 0, 0, 1] });
      // la languette rouge au bout de la bande : la forme fait partie de l'affiche
      const tab = 2 * (T.hh + padY) * 0.62;
      rows.push({ z: 99991, v: [T.x - T.hw - padX - tab * 0.15, T.y + T.hh + padY - tab * 0.2, tab, tab, 0, TYPES.S, 1, 2, 0, 0, 1, 1, 0, 0, 0, 1] });
    }
    if (an.station) {
      const S = an.station, padX = 0.06, padY = 0.035, hh = S.hh + padY + 0.06;
      rows.push({ z: 99992, v: [S.x, S.y + 0.06 / 1, 2 * (S.hw + padX), 2 * hh, 0, TYPES.B, 3, 2, 0, 0, 1, 1, 0, 0, 0, 1] });
      rows.push({ z: 99993, v: [S.x, S.y - hh + 0.06 - 0.018, 2 * (S.hw + padX) * 0.7, 0.01, 0, TYPES.L, 1, 2, 4, 0, 1, 1, 0, 0, 0, 1] });
    }
    rows.sort((a, b) => a.z - b.z);
    const n = Math.min(MAX, rows.length), D = this._data;
    for (let i = 0; i < n; i++) D.set(rows[i].v, i * 16);
    this._SU.uCount.value = n;
    this._dataTex.needsUpdate = true;
    this._rows = rows;

    // le papier (le drop l'éclaircit d'un cran)
    this._PU.uBase.value = PAPER_BASE + (1 - PAPER_BASE) * Math.min(1, this._flash);

    // la transition en cours
    const tr = this._trans, DU = this._DU, A = safeZoneUniforms.uAspect.value || 16 / 9;
    if (tr && tr.mode !== 'glide') {
      const u = clock - tr.start;
      if (tr.mode === 'print') {
        const sw = (k) => -A - 0.05 + (2 * A + 0.1) * easeInOut(clamp01(u - k));
        this._SU.uSweep.value.set(sw(0), sw(1), sw(2), sw(3));
        this._SU.uPrintOn.value = 1;
        const active = Math.min(3, Math.floor(Math.max(0, u)));
        DU.uMode.value = 1; DU.uA.value.set(sw(0), sw(active), 0, 0);
      } else if (tr.mode === 'iris') {
        DU.uMode.value = 2; DU.uA.value.set(tr.center.x, tr.center.y, tr.r0 + (tr.rmax - tr.r0) * Math.pow(easeInOut(u / tr.dur), 1.4), 0);
      } else {
        // CUT : la fente s'ouvre (un demi-temps), puis les deux moitiés glissent hors de l'écran
        const open = clamp01(u / 0.5), go = clamp01((u - 0.5) / (tr.dur - 0.5));
        const gap = 0.012 * easeInOut(open);
        const slide = tr.reach * go * go;
        const d = tr.dir, nn = [-d[1], d[0]];
        DU.uMode.value = 3; DU.uA.value.set(tr.point[0], tr.point[1], d[0], d[1]);
        DU.uB.value.set(nn[0] * gap + d[0] * slide + nn[0] * slide * 0.35, nn[1] * gap + d[1] * slide + nn[1] * slide * 0.35,
          -nn[0] * gap - d[0] * slide - nn[0] * slide * 0.35, -nn[1] * gap - d[1] * slide - nn[1] * slide * 0.35);
      }
    } else { DU.uMode.value = 0; this._SU.uPrintOn.value = 0; }
  }

  // le rendu de l'affiche dans la cible privée, au moment où l'affichage est dessiné
  _renderPoster(renderer) {
    const target = renderer.getRenderTarget();
    if (target) this._res.set(target.width, target.height); else renderer.getDrawingBufferSize(this._res);
    const w = Math.max(16, Math.round(this._res.x)), h = Math.max(16, Math.round(this._res.y));
    for (const rt of this._rt) if (rt.width !== w || rt.height !== h) rt.setSize(w, h);
    this._measureAnchors(renderer);
    const cur = this._rt[this._cur];
    if (!this._paused || this._needRender) {
      renderer.setRenderTarget(cur);
      renderer.render(this._pscene, this._pcam);
      renderer.setRenderTarget(target);
      this._needRender = false;
    }
    this._DU.uCur.value = cur.texture;
    this._DU.uSnap.value = this._rt[this._cur ^ 1].texture;
  }

  getProbe() {
    const shapes = this._live.filter((e) => e.kind === 'shape');
    const fams = {}; for (const e of shapes) if (e.fam) fams[e.fam] = (fams[e.fam] || 0) + 1;
    return {
      tier: this._tier, clock: +this._clock.toFixed(3), comp: this._compIdx, poster: this._posterNow && this._posterNow.def.name,
      elements: shapes.length, micro: shapes.filter((e) => e.micro).length, families: fams, famA: this._posterNow && this._posterNow.famA,
      glyphs: this._live.filter((e) => e.kind === 'glyph').map((e) => ({ ch: e.base.ch, h: +e.base.h.toFixed(2) })).length,
      text: this._posterNow && this._posterNow.text, maxLetterH: Math.max(0, ...this._live.filter((e) => e.kind === 'glyph').map((e) => e.base.h * COL / 2)),
      trans: this._trans ? this._trans.mode : null, drop: !!this._drop, fam: this._famState, lowS: this._lowS, midS: this._midS, anchors: this._anchors, stats: JSON.parse(JSON.stringify(this._stats))
    };
  }

  dispose() {
    if (this._scene) {
      this._scene.remove(this._display);
      this._scene.background = this._prevBackground || null;
    }
    if (this._display) { this._display.onBeforeRender = () => {}; this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._paper) { this._paper.geometry.dispose(); this._paper.material.dispose(); }
    for (const m of this._passes || []) m.material.dispose();
    if (this._geo) this._geo.dispose();
    for (const rt of this._rt || []) rt.dispose();
    if (this._dataTex) this._dataTex.dispose();
    if (this._atlasTex) this._atlasTex.dispose();
    if (typeof window !== 'undefined' && window.__bauhaus === this) window.__bauhaus = null;
    this._scene = null;
  }
}
